import type { ServiceForSelectedServiceType } from '@common/interfaces/processStartWizard';
import { ServiceCode } from '@common/enums';
import { never } from '@common/utils/never';
import { useFrontendSettings } from '@frontend/utils/hooks/useFrontendSettings';
import type { IServiceDataItem } from './getServiceData';

export function getServiceDataForEpisodeDermatology(
    service: ServiceForSelectedServiceType,
    serviceCode:
        | ServiceCode.DermaOth
        | ServiceCode.DermaC
        | ServiceCode.DermaCsc
        | ServiceCode.DermaCwond
        | ServiceCode.DermaSnelC
        | ServiceCode.DermaSnelCsc
): Partial<IServiceDataItem> {
    const dermatologySnelConsultTimeLimit = useFrontendSettings().frontendSettings.dermatologySnelConsultTimeLimit;

    switch (serviceCode) {
        case ServiceCode.DermaOth:
            return {
                costs: 'geen kosten',
                steps: [
                    'Verstuur het Thuisconsult naar de patiënt',
                    "Patiënt neemt thuis foto's en vult vragenlijst in",
                    'Je ontvangt de gegevens en beoordeelt de klacht',
                    'Vraag eventueel direct een meekijkconsult aan',
                ],
                subTitle: (
                    <>
                        <strong>Patiënt maakt thuis foto's</strong> die je op afstand kan beoordelen
                    </>
                ),
                title: 'Thuisconsult',
                waitingTime: 'direct beschikbaar',
            };

        case ServiceCode.DermaC:
            return {
                costs: 'eigen risicovrij',
                order: 1,
                steps: [
                    "Maak foto's en neem anamnese af",
                    'Stel je vraag aan een Ksyos specialist',
                    'De specialist beoordeelt digitaal',
                    'Je ontvangt de uitslag in het Ksyos platform en na het afsluiten via Edifact (in je HIS)',
                ],
                subTitle: (
                    <>
                        Voor de beoordeling van <strong>algemene huidklachten</strong> zoals eczeem, acne, rode vlekken
                        en schilfers
                    </>
                ),
                title: 'Algemeen consult',
                waitingTime: '2-3 werkdagen',
            };

        case ServiceCode.DermaSnelC:
            return {
                availableDuring: 'Op werkdagen van 8:00 - 17:00',
                costs: 'eigen risicovrij',
                isNew: true,
                order: 4,
                steps: [
                    "Maak foto's en neem anamnese af",
                    'Stel je vraag aan een Ksyos dermatoloog',
                    'De specialist beoordeelt digitaal',
                    'Je ontvangt de uitslag via Edifact (in je HIS)',
                ],
                subTitle: (
                    <>
                        <strong>Binnen {dermatologySnelConsultTimeLimit} minuten</strong> een beoordeling van{' '}
                        <strong>algemene huidklachten</strong> zoals eczeem, acne, rode vlekken en schilfers
                    </>
                ),
                title: 'Snelconsult algemeen',
                waitingTime: <strong>{dermatologySnelConsultTimeLimit} minuten</strong>,
            };

        case ServiceCode.DermaCsc:
            return {
                costs: 'eigen risicovrij',
                order: 2,
                steps: [
                    "Maak foto's met een dermatoscoop en neem anamnese af",
                    'Stel je vraag aan een Ksyos specialist',
                    'De specialist beoordeelt digitaal',
                    'Je ontvangt de uitslag in het Ksyos platform en na het afsluiten via Edifact (in je HIS)',
                ],
                subTitle: (
                    <>
                        Voor de beoordeling van <strong>moedervlekken</strong>
                    </>
                ),
                title: 'Dermatoscopie consult',
                waitingTime: '2-3 werkdagen',
            };

        case ServiceCode.DermaSnelCsc:
            return {
                availableDuring: 'Op werkdagen van 8:00 - 17:00',
                costs: 'eigen risicovrij',
                isNew: true,
                order: 5,
                steps: [
                    "Maak foto's met een dermatoscoop en neem anamnese af",
                    'Stel je vraag aan een Ksyos dermatoloog',
                    'De specialist beoordeelt digitaal',
                    'Je ontvangt de uitslag via Edifact (in je HIS)',
                ],
                subTitle: (
                    <>
                        <strong>Binnen {dermatologySnelConsultTimeLimit} minuten</strong> beoordeling van{' '}
                        <strong>moedervlekken</strong>
                    </>
                ),
                title: 'Snelconsult dermatoscopie',
                waitingTime: <strong>{dermatologySnelConsultTimeLimit} minuten</strong>,
            };

        case ServiceCode.DermaCwond:
            return {
                costs: 'eigen risicovrij',
                order: 3,
                steps: [
                    "Maak foto's en neem anamnese af",
                    'Stel je vraag aan een Ksyos wondspecialist',
                    'De specialist beoordeelt digitaal',
                    'Je ontvangt de uitslag in het Ksyos platform en na het afsluiten via Edifact (in je HIS)',
                ],
                subTitle: (
                    <>
                        Voor de beoordeling van <strong>(complexe) wonden</strong>
                    </>
                ),
                title: 'Wondconsult',
                waitingTime: '2-3 werkdagen',
            };

        default:
            never(serviceCode);
    }
}
